import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { FieldErrors, FieldValues, UseFormTrigger } from 'react-hook-form';
import { StepConfig } from './shared-form-stepper';
import { ZodObject, ZodRawShape, ZodTypeAny } from 'zod';
import ZodValidationDialog from './zod-validation-dialog';
import SimpleFormHeader from './simple-form-header';
import { Status } from './status-chip';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

interface SimpleFormPageProps<T extends FieldValues> {
  title: string;
  trigger: UseFormTrigger<Partial<T>> | UseFormTrigger<T>;
  steps: StepConfig[];
  stepper?: ReactNode;
  content: ReactNode;
  handleBack?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleNext?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleDiscard?: () => void;
  handleSubmit: () => void;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  isDisableSubmit?: boolean;
  isLoadingSubmit: boolean;
  submitButtonText?: string;
  setStepCompleted: Dispatch<SetStateAction<boolean[]>>;
  firstIdWithError?: string;
  setExpandedIds?: Dispatch<SetStateAction<Set<string>>>;
  errors: FieldErrors<T>;
  lastSavedDate?: number;
  hasBeenSaved?: boolean;
  handleSaveAndClose?: () => void;
  isSaving?: boolean;
  isFormDirty?: boolean;
  status?: Status;
}

export function getSchemaKeys(
  schema: ZodObject<ZodRawShape>,
  parentKey = ''
): string[] {
  const keys: string[] = [];

  for (const key in schema.shape) {
    const value = schema.shape[key] as ZodTypeAny;
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    const unwrapped = unwrapZod(value);

    if (unwrapped instanceof ZodObject) {
      keys.push(...getSchemaKeys(unwrapped, fullKey));
    } else {
      keys.push(fullKey);
    }
  }

  return keys;
}

export function unwrapZod(zod: ZodTypeAny): ZodTypeAny {
  if ('_def' in zod) {
    const def: any = (zod as any)._def;
    if (def.innerType) return unwrapZod(def.innerType);
    if (def.schema) return unwrapZod(def.schema);
  }
  return zod;
}

export default function SimpleFormPage<T extends FieldValues>({
  title,
  trigger,
  steps,
  stepper,
  content,
  handleBack,
  handleNext,
  handleDiscard,
  handleSubmit,
  activeStep,
  setActiveStep,
  isDisableSubmit,
  isLoadingSubmit,
  submitButtonText,
  setStepCompleted,
  firstIdWithError,
  setExpandedIds,
  errors,
  lastSavedDate,
  handleSaveAndClose,
  isSaving,
  isFormDirty,
  status,
}: SimpleFormPageProps<T>) {
  const theme = useTheme();
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  const handleDefaultNext = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    const fields = steps[activeStep].schema
      ? getSchemaKeys(steps[activeStep].schema as any)
      : [];
    const isValid = await trigger(fields as any);

    if (isValid) {
      setActiveStep((prevState) => prevState + 1);
      setStepCompleted((prevState) => {
        const newCompleted = [...prevState];
        newCompleted[activeStep] = true;
        return newCompleted;
      });
    }
    if (firstIdWithError && setExpandedIds) {
      setExpandedIds(new Set([firstIdWithError]));

      setTimeout(() => {
        const element = document.getElementById(firstIdWithError);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };
  const handleDefaultBack = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    const fields = steps[activeStep].schema
      ? getSchemaKeys(steps[activeStep].schema as any)
      : [];
    await trigger(fields as any);
    setActiveStep((prevState) => prevState - 1);
    setStepCompleted((prevState) => {
      const newCompleted = [...prevState];
      newCompleted[activeStep] = true;
      return newCompleted;
    });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && handleDiscard) {
        event.preventDefault();
        handleDiscard();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleDiscard]);

  return (
    <>
      <Box
        component="form"
        onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (activeStep === steps.length - 1) {
            const isValid = await trigger();
            if (!isValid) {
              if (steps.length > 1) setShowWarningDialog(true);
            } else {
              handleSubmit();
            }
            return;
          }
        }}
        sx={{ height: '100%' }}
        noValidate
      >
        <Card sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <Grid container direction="column" sx={{ height: '100%' }}>
            {/* Title */}
            <SimpleFormHeader
              title={title}
              lastSavedDate={lastSavedDate}
              isSaving={isSaving}
              status={status}
            />

            <Grid
              container
              sx={{ flex: 1, minHeight: 0, overflow: 'hidden', width: '100%' }}
            >
              {/* Stepper */}
              {stepper && (
                <Grid
                  sx={{
                    p: 3,
                    pl: 5,
                    pt: 0,
                    pr: 2,
                    minHeight: 0,
                    maxHeight: '90%',
                    overflowY: 'auto',
                    width: '18%',
                  }}
                >
                  {stepper}
                </Grid>
              )}
              {/* Content Area */}
              <Grid
                sx={{
                  p: 4,
                  pt: 0,
                  pl: 0,
                  pr: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  width: '82%',
                  minHeight: 0,
                }}
              >
                <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, p: 2 }}>
                  {content}
                </Box>
              </Grid>
            </Grid>

            {/* Footer */}
            <Grid
              sx={{
                p: 3,
                pt: 0,
                pb: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={2}>
                <Button
                  sx={{
                    color: theme.palette.text.secondary,
                    border: '1px solid',
                    borderColor: theme.palette.text.secondary,
                  }}
                  onClick={handleDiscard}
                >
                  Cancel
                </Button>
                {handleSaveAndClose && (
                  <Button
                    variant="outlined"
                    disabled={!isFormDirty}
                    sx={{
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                    }}
                    onClick={handleSaveAndClose}
                  >
                    Save and Close
                  </Button>
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                {!(activeStep === 0) && (
                  <Button onClick={handleBack ? handleBack : handleDefaultBack}>
                    Back
                  </Button>
                )}
                <Button
                  variant="contained"
                  loading={isLoadingSubmit}
                  disabled={isDisableSubmit ?? false}
                  type={activeStep === steps.length - 1 ? 'submit' : 'button'}
                  onClick={
                    activeStep === steps.length - 1
                      ? undefined
                      : handleNext ?? handleDefaultNext
                  }
                >
                  {activeStep === steps.length - 1
                    ? submitButtonText ?? 'Finish'
                    : 'Continue'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Card>
      </Box>
      {showWarningDialog && (
        <ZodValidationDialog
          open={showWarningDialog}
          handleClose={() => setShowWarningDialog(false)}
          errors={errors}
        />
      )}
    </>
  );
}
